import React, { useEffect, useState, useContext } from "react";
import {
  Grid,
  Container,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import { useParams } from "react-router-dom";
import api from "../../services/api";
import LeftColumn from "./LeftColumn";
import RightColumn from "./RightColumn";
import calculateCakePrice from "../../utils/calculateCakePrice";
import SharedCartPreview from "../../pages/Cart/SharedCartPreview";
import { CartContext } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { buildLineId } from "../../utils/lineId";

const SpecialtyCakeDetailPage = () => {
  const { id: cakeId } = useParams();
  const [cake, setCake] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState("");
  const [secondaryImages, setSecondaryImages] = useState([]);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedFlavor, setSelectedFlavor] = useState("");
  const [showFlavorModal, setShowFlavorModal] = useState(false);
  const [showCartPreview, setShowCartPreview] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  const [customizations, setCustomizations] = useState({});

  const { addToCart } = useContext(CartContext);

  const totalPrice = calculateCakePrice(
    cake,
    selectedSize,
    selectedFlavor,
    customizations
  );

  useEffect(() => {
    document.title = "Specialty Cake - Customize";
  }, []);

  useEffect(() => {
    fetchCakeDetails();
  }, [cakeId]);

  useEffect(() => {
    if (cake?.defaultCustomizations) {
      setCustomizations(cake.defaultCustomizations);
    }
  }, [cake]);

  useEffect(() => {
    if (!cake) return;
    setPriceLoading(true);
    const timeout = setTimeout(() => setPriceLoading(false), 700);
    return () => clearTimeout(timeout);
  }, [selectedSize, selectedFlavor]);

  const fetchCakeDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/specialty-cakes/${cakeId}`);
      const cakeData = response.data;
      setCake(cakeData);

      if (window.fbq) {
        window.fbq("track", "ViewContent", {
          content_name: cakeData.displayName,
          content_ids: [cakeData._id],
          content_type: "product",
          value: calculateCakePrice(
            cakeData,
            selectedSize,
            selectedFlavor,
            customizations
          ),
          currency: "CAD",
        });
      }

      if (window.gtag) {
        window.gtag("event", "view_item", {
          currency: "CAD",
          value: calculateCakePrice(
            cakeData,
            selectedSize,
            selectedFlavor,
            customizations
          ),
          items: [
            {
              item_id: cakeData._id,
              item_name: cakeData.displayName,
              item_category: cakeData.category || "specialtyCake",
              price: calculateCakePrice(
                cakeData,
                selectedSize,
                selectedFlavor,
                customizations
              ),
            },
          ],
        });
      }

      const primary = cakeData.images.primary;
      const secondary = cakeData.images.secondary || [];
      const filteredSecondary = secondary.filter((img) => img !== primary);

      setSelectedImage(primary);
      setSecondaryImages(filteredSecondary);
    } catch (error) {
      console.error("Error fetching cake:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetCustomizations = () => {
    setCustomizations(cake.defaultCustomizations || {});
  };

  const handleAddToCart = async () => {
    const isNumberCake = cake.name === "number_cake";
    const characters = customizations.characters || [];
    const hasValidCharacters =
      Array.isArray(characters) && characters.some((c) => c?.char?.trim());

    if (
      !selectedSize ||
      !selectedFlavor ||
      (isNumberCake && !hasValidCharacters)
    ) {
      alert("Please select a size, a flavor, and at least one character.");
      return;
    }

    const selectedSizeObj = cake.sizes.find((s) => s._id === selectedSize);
    const selectedFlavorObj = cake.flavors.find(
      (f) => f._id === selectedFlavor
    );
    if (!selectedSizeObj || !selectedFlavorObj) return;

    const calculatedPrice = calculateCakePrice(
      cake,
      selectedSize,
      selectedFlavor,
      customizations
    );

    // 1) User-facing customizations (shown in cart/checkout)
    const uiCustomizations = [
      { key: "size", label: "Size", value: selectedSizeObj.displayName },
      { key: "flavor", label: "Flavor", value: selectedFlavorObj.displayName },
      ...Object.entries(customizations).flatMap(([key, value]) => {
        const field = cake.customizationFields.find((f) => f.key === key);

        if (key === "characters" && Array.isArray(value)) {
          const chars = value
            .filter((c) => c?.char)
            .map((c) => c.char)
            .join("");
          if (!chars) return [];
          return [
            {
              key: "characters",
              label: field?.label || "Characters",
              value: chars,
            },
          ];
        }

        return [{ key, label: field?.label || key, value }];
      }),
    ];

    // 2) Minimal, normalized snapshot for a stable lineId (avoid label text)
    const idCustomizations = uiCustomizations.map((c) => ({
      key: c.key,
      value: String(c.value).toLowerCase(),
    }));

    const type = cake.category || "specialtyCake";
    const lineId = buildLineId({
      productId: cake._id,
      type,
      options: {
        sizeId: selectedSizeObj._id,
        flavorId: selectedFlavorObj._id,
        customizations: idCustomizations,
      },
    });

    // 3) Cart line
    const newItem = {
      // identity
      lineId,
      productId: cake._id,
      type,
      category: cake.category || "specialtyCake",
      subcategory: null,

      // display/meta
      name: cake.name,
      displayName: cake.displayName,
      image: selectedImage,

      // options
      size: {
        _id: selectedSizeObj._id,
        name: selectedSizeObj.displayName,
        tierCount: selectedSizeObj.tierCount,
      },
      flavor: {
        _id: selectedFlavorObj._id,
        name: selectedFlavorObj.displayName,
      },

      // details (plural + singular for compatibility with older code paths)
      customizations: uiCustomizations,
      customization: uiCustomizations,

      // pricing — listPrice is your pre-discount line unit; unitPrice starts same
      listPrice: Number(calculatedPrice),
      unitPrice: Number(calculatedPrice),
      quantity: 1,
      totalPrice: Number(calculatedPrice),
      itemsPerSet: 1,
    };

    // 4) Add via context (handles backend sync & returns status)
    const result = await addToCart(newItem);
    setShowCartPreview(true);

    // (Optional) show snackbars if you wire a snackbar here:
    // if (result.ok && result.synced) snackbar.success("Item added to cart");
    // else if (result.ok) snackbar.warning("Added (sync failed)");
    // else snackbar.error("Failed to add item");

    // 5) Analytics
    if (window.fbq) {
      window.fbq("track", "AddToCart", {
        content_name: cake.displayName,
        content_ids: [cake._id],
        content_type: "product",
        value: calculatedPrice,
        currency: "CAD",
      });
    }
    if (window.gtag) {
      window.gtag("event", "add_to_cart", {
        currency: "CAD",
        value: calculatedPrice,
        items: [
          {
            item_id: cake._id,
            item_name: cake.displayName,
            item_category: cake.category || "specialtyCake",
            item_variant: selectedSizeObj.displayName,
            item_flavor: selectedFlavorObj.displayName,
            quantity: 1,
            price: calculatedPrice,
          },
        ],
      });
    }
  };

  // 🔁 Loading fallback UI
  if (loading) {
    return (
      <Box
        sx={{
          height: "70vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
        }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }} variant="body1" color="textSecondary">
          Loading cake details...
        </Typography>
      </Box>
    );
  }

  // 🔁 Only render actual page once cake is loaded
  return (
    <Container disableGutters maxWidth="lg" sx={{ mt: 5, mb: 8, px: 2 }}>
      <Grid container spacing={6}>
        <Grid item xs={12} md={5}>
          <LeftColumn
            selectedImage={selectedImage}
            setSelectedImage={setSelectedImage}
            secondaryImages={secondaryImages}
            handleAddToCart={handleAddToCart}
            priceLoading={priceLoading}
            totalPrice={totalPrice}
            selectedSize={selectedSize}
            selectedFlavor={selectedFlavor}
            customizations={customizations}
            cake={cake}
          />
        </Grid>
        <Grid item xs={12} md={7}>
          <RightColumn
            cake={cake}
            selectedSize={selectedSize}
            setSelectedSize={setSelectedSize}
            selectedFlavor={selectedFlavor}
            setSelectedFlavor={setSelectedFlavor}
            showFlavorModal={showFlavorModal}
            setShowFlavorModal={setShowFlavorModal}
            totalPrice={totalPrice}
            handleAddToCart={handleAddToCart}
            priceLoading={priceLoading}
            customizations={customizations}
            setCustomizations={setCustomizations}
            onResetCustomizations={handleResetCustomizations}
          />
        </Grid>
      </Grid>

      <SharedCartPreview
        show={showCartPreview}
        setShowCartPreview={setShowCartPreview}
      />
    </Container>
  );
};

export default SpecialtyCakeDetailPage;
